import API from '@/utils/API'
import { verifyResultKeys, verifyResultReasons } from '@/utils/VerifyResults'
import { ActionContext } from 'vuex'

const ACCOUNT_NAME_ATTRS = {
  FAMILY_NAME: 'account_family_name',
  FIRST_NAME: 'account_first_name',
}

const DELIMITER_BETWEEN_ACCOUNT_NAME_PARTS = '　'

const state = {
  depositBankAccount: {
    bank_name: null,
    bank_code: null,
    branch_code: null,
    account_type: null,
    account_number: null,
    account_name: null,
  },
  presentMasterBanks: [],
  verifyResults: [],
}

interface DepositBankAccount {
  bank_name: string | null
  bank_code: string | null
  branch_code: string | null
  account_type: string | null
  account_number: string | null
  account_name: string | null | undefined
}

interface Bank {
  // TODO: Duplicate
  bank_code: string
  bank_name: string
}

interface State {
  depositBankAccount: DepositBankAccount
  presentMasterBanks: any[]
  verifyResults: any[]
}

type ModuleActionContext = ActionContext<State, {}>

interface DepositBankAccountModifyOperation {
  name: keyof DepositBankAccount
  value: string | null
}

const isInputCompleted = (state: State) => {
  const input = state.depositBankAccount
  return (
    input.bank_name &&
    input.bank_code &&
    input.branch_code &&
    input.account_type &&
    input.account_number &&
    input.account_name &&
    findAccountFamilyName(input.account_name) &&
    findAccountFirstName(input.account_name)
  )
}

const spiltBankAccountName = (accountName: string | null | undefined) => {
  if (!accountName) {
    return []
  }
  return accountName.split(DELIMITER_BETWEEN_ACCOUNT_NAME_PARTS) // 全角スペース
}

const findAccountFamilyName = (accountName: string | null | undefined) => {
  const names = spiltBankAccountName(accountName)
  if (names.length < 1) {
    return ''
  }
  return names[0]
}

const findAccountFirstName = (accountName: string | null | undefined) => {
  const names = spiltBankAccountName(accountName)
  if (names.length < 2) {
    return ''
  }
  return names[1]
}

// getters
const getters = {
  isVerifyResultExists: (state: State) => {
    return state.verifyResults && state.verifyResults.length > 0
  },
  verifyResultKeys: (state: State) => {
    return verifyResultKeys(state.verifyResults)
  },
  verifyResultReasons: (state: State) => {
    return verifyResultReasons(state.verifyResults)
  },
  canSubmitInput: (state: State) => {
    return isInputCompleted(state)
  },
  accountFamilyName: (state: State) =>
    findAccountFamilyName(state.depositBankAccount.account_name),
  accountFirstName: (state: State) =>
    findAccountFirstName(state.depositBankAccount.account_name),
}

const actions = {
  async fetchDepositBankAccount({ commit }: ModuleActionContext) {
    const request = API.Request.depositAccountBankGet()
    const response = await API.load(request)
    if (response.DepositBankAccount) {
      commit('setDepositBankAccount', response.DepositBankAccount)
    } else {
      console.error(response) // eslint-disable-line no-console
    }
  },
  selectBank({ commit }: ModuleActionContext, bank: Bank) {
    if (bank) {
      commit('setBank', bank)
    }
  },
  async postDepositAccountBankUpdate({ commit, state }: ModuleActionContext) {
    if (!isInputCompleted(state)) {
      return
    }
    const params = {
      bank_code: state.depositBankAccount.bank_code,
      branch_code: state.depositBankAccount.branch_code,
      account_type: state.depositBankAccount.account_type,
      account_number: state.depositBankAccount.account_number,
      account_name: state.depositBankAccount.account_name,
    }
    const request = API.Request.depositAccountBankUpdate(params)
    const response = await API.load(request)

    commit('setVerifyResults', response.VerifyResults)
    if (response.VerifyResults && response.VerifyResults.length > 0) {
      console.error(response) // eslint-disable-line no-console
      throw new Error()
    }
    return response.DepositBankAccount
  },
  inputDepositBankAccountAttr(
    { commit }: ModuleActionContext,
    { name, value }: DepositBankAccountModifyOperation
  ) {
    if (
      [ACCOUNT_NAME_ATTRS.FAMILY_NAME, ACCOUNT_NAME_ATTRS.FIRST_NAME].includes(
        name
      )
    ) {
      commit('updateDepositBankAccountName', { name, value })
    } else {
      commit('updateDepositBankAccountAttr', { name, value })
    }
  },
}

const mutations = {
  setDepositBankAccount(state: State, depositBankAccount: DepositBankAccount) {
    state.depositBankAccount = depositBankAccount
  },
  setBank(state: State, bank: Bank) {
    state.depositBankAccount = Object.assign(state.depositBankAccount, {
      bank_name: bank.bank_name,
      bank_code: String(bank.bank_code), // master_bank系APIレスポンスではNumberだが、deposit_account系APIのリクエストパラメタとしてはStringが必要
    })
  },
  updateDepositBankAccountAttr(
    state: State,
    { name, value }: DepositBankAccountModifyOperation
  ) {
    state.depositBankAccount[name] = value
  },
  updateDepositBankAccountName(
    state: State,
    { name, value }: DepositBankAccountModifyOperation
  ) {
    const createAccountName = (
      accountNamePrev: string | null | undefined,
      { name, value }: DepositBankAccountModifyOperation
    ) => {
      if (name == ACCOUNT_NAME_ATTRS.FAMILY_NAME) {
        return [value, findAccountFirstName(accountNamePrev)].join(
          DELIMITER_BETWEEN_ACCOUNT_NAME_PARTS
        )
      } else if (name == ACCOUNT_NAME_ATTRS.FIRST_NAME) {
        return [findAccountFamilyName(accountNamePrev), value].join(
          DELIMITER_BETWEEN_ACCOUNT_NAME_PARTS
        )
      }
    }
    state.depositBankAccount.account_name = createAccountName(
      state.depositBankAccount.account_name,
      { name, value }
    )
  },
  setVerifyResults(state: State, verifyResults: any[]) {
    state.verifyResults = verifyResults
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
